import { useDispatch, useSelector } from 'react-redux'
import usePracticeConfiguration from './usePracticeConfiguration'
import { PMS } from '../consts'
import { useCallback, useEffect, useMemo } from 'react'
import { isUserOfAnyPracticeRole, isUserOfRole, validateTokenExpiration } from 'utils/authUtils'
import actions from 'actions'
import { HAS_STATIC_DATA, PMS_INTEGRATIONS } from 'consts/pmsConsts'
import DolphinNewAppointment from 'components/Patients/PatientCard/AppointmentsTab/PmsNewAppointment/DolphinNewAppointment'
import DolphinIcon from 'components/common/icons/DolphinIcon'
import { Roles } from 'consts/authConsts'

const resolvePmsKey = externalIds => {
  if (!externalIds) {
    return null
  }

  const parsedExternalIds = typeof externalIds === 'string' ? JSON.parse(externalIds || '{}') : externalIds

  const pmsKey = Object.keys(parsedExternalIds || {}).find(key => Object.values(PMS.PMS_INTEGRATIONS).includes(key))

  if (!pmsKey) {
    return null
  }

  return parsedExternalIds?.[pmsKey] ? pmsKey : null
}

export default () => {
  const dispatch = useDispatch()
  const { ownerAppFeatureFlags } = usePracticeConfiguration()

  const doctorExternalIds = useSelector(state =>
    JSON.parse((state.practiceReducer.accountOwner || state.profileReducer?.doctor)?.user?.externalIds || '{}')
  )
  const accountOwnerUsername = useSelector(state => state.practiceReducer.accountOwner?.username)
  const patient = useSelector(state => state.patientsReducer?.patientCard?.patient)
  const patientDoctor = patient?.doctor
  const patientDoctorExternalIds = useMemo(
    () => JSON.parse(patientDoctor?.user?.externalIds || '{}'),
    [patientDoctor?.user?.externalIds]
  )
  const patientExternalIds = useSelector(state =>
    JSON.parse(state.patientsReducer?.patientCard?.patient?.user?.externalIds || '{}')
  )
  const { pmsType, hasFetchedStaticData, appointmentTypes } = useSelector(state => state.pmsReducer)

  const ownerPmsKey = useMemo(() => resolvePmsKey(doctorExternalIds), [doctorExternalIds])
  const patientDoctorPmsKey = useMemo(
    () => (!ownerPmsKey ? resolvePmsKey(patientDoctorExternalIds) : null),
    [ownerPmsKey, patientDoctorExternalIds]
  )
  const doctorPms = ownerPmsKey || patientDoctorPmsKey || null
  const onBehalfUsername = ownerPmsKey ? accountOwnerUsername : patientDoctor?.username

  const getPmsToken = useCallback(
    () => validateTokenExpiration(localStorage.getItem(`${doctorPms}_token`)),
    [doctorPms]
  )
  const renderPmsNewAppointment = useCallback(
    props => {
      switch (pmsType) {
        case PMS_INTEGRATIONS.dolphin:
          return <DolphinNewAppointment {...props} />
        default:
          return null
      }
    },
    [pmsType]
  )
  const renderPmsIcon = useCallback(() => {
    switch (pmsType) {
      case PMS_INTEGRATIONS.dolphin:
        return <DolphinIcon />
      default:
        return <></>
    }
  }, [pmsType])

  const isPracticeUser = useMemo(() => isUserOfAnyPracticeRole(), [])
  const isInternalViewer = useMemo(
    () =>
      isUserOfRole([
        Roles.HI,
        Roles.HIAdmin,
        Roles.Admin,
        Roles.OpsAdmin,
        Roles.DevAdmin,
        Roles.Sales,
        Roles.DsoSupport,
        Roles.Distributor,
        Roles.GI
      ]),
    []
  )
  const hasPmsWriteAccess = isPracticeUser
  const shouldRequestOnBehalf = !hasPmsWriteAccess && isInternalViewer && !!onBehalfUsername

  const authenticatePms = useCallback(() => {
    if (!doctorPms) {
      return
    }

    const pmsToken = getPmsToken()
    dispatch(
      actions.pmsAuthentication({
        pmsType: doctorPms,
        pmsToken,
        onBehalfOf: shouldRequestOnBehalf ? onBehalfUsername : null
      })
    )
  }, [dispatch, doctorPms, getPmsToken, onBehalfUsername, shouldRequestOnBehalf])

  const loadPmsStaticData = useCallback(() => {
    if (getPmsToken() && HAS_STATIC_DATA.includes(pmsType) && !hasFetchedStaticData) {
      dispatch(actions.pmsFetchStaticData())
    }
  }, [dispatch, getPmsToken, hasFetchedStaticData, pmsType])

  const getAppointmentType = useCallback(
    apt => appointmentTypes?.find(aptType => aptType.id === apt.appointmentTypeId)?.name,
    [appointmentTypes]
  )

  useEffect(() => {
    if (!pmsType && !!doctorPms) {
      dispatch(actions.setPmsType(doctorPms))
    }
  }, [dispatch, doctorPms, pmsType])

  return {
    pmsIntegrationsFF: ownerAppFeatureFlags.pmsIntegrations,
    doctorExternalIds,
    doctorPms,
    patientExternalIds,
    patientPmsID: doctorPms ? patientExternalIds[doctorPms] : null,
    renderPmsNewAppointment,
    authenticatePms,
    loadPmsStaticData,
    pmsType,
    getPmsToken,
    renderPmsIcon,
    getAppointmentType,
    hasPmsWriteAccess
  }
}
